import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { withLocalize } from 'react-localize-redux';
import Icon, { IconTypes } from '../../../components/foundations/Icon';
import BookmarkPopover from '../../../components/reportsWidgets/BookmarkPopover';
import { Reports } from '../../../services';
import { useUrlParams } from '../../../utils/useUrlParamsHook';
import { useAsync } from '../../../utils/customHooks';
import css from './TagsVisibilityHeader.css';
import Tooltip from '../../../components/molecules/Tooltip';
import { getUserData } from '../../../mta/selectors';
import { triggerBookmarkEvent } from '../../../sagas/bookmarks';
import { setActionType, updateBookmarkData } from '../../../actions/reports';
import { reportsWithoutConfig } from '../../../utils/reports';

export const BOOKMARK_RUN_QUERY_MESSAGE = 'STATIC.PAGES.TAGS_VISIBILITY.SAVE_BOOKMARK.RUN_QUERY_MESSAGE';
export const UNAUTHORIZED_BOOKMARK_EDITING = 'STATIC.BUTTONS.TOOLTIP.DISABLE_SAVE';
export const BOOKMARK_MANDATORY_NAME_ERROR = 'STATIC.PAGES.TAGS_VISIBILITY.SAVE_BOOKMARK.MANDATORY_NAME_ERROR';
const BOOKMARKS_NOT_AVAILABLE = 'STATIC.PAGES.TAGS_VISIBILITY.SAVE_BOOKMARK.NOT_AVAILABLE';
const ReportsAPI = new Reports();

const BookmarkPopoverContainer = ({
    translate,
    hasQueryBeenDone,
    activeBookmark,
    startDate,
    endDate,
    disabled = false,
}) => {
    const [showBookmarkPopover, setShowBookmarkPopover] = useState(false);
    const [bookmarkValidationError, setBookmarkValidationError] = useState(null);
    const userName = useSelector(state => getUserData(state).username);
    const dispatch = useDispatch();

    const saveIconRef = useRef();

    const { getParamsObject, setParamsObject } = useUrlParams();
    const [{ data: saveBookmarkResponse, error: saveBookmarkResponseError }, saveBookmark] = useAsync();

    const { uuid: bookmarkId, creator: bookmarkCreator } = activeBookmark || {};

    useEffect(() => {
        const handleClickOutside = event => {
            if (saveIconRef.current && !saveIconRef.current.contains(event.target)) {
                setShowBookmarkPopover(false);
            }
        };

        if (showBookmarkPopover) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showBookmarkPopover]);

    useEffect(() => {
        const updatedBookmark = saveBookmarkResponse?.bookmark;
        if (!updatedBookmark) return;

        setShowBookmarkPopover(false);
        const bookmarkEventType = saveBookmarkResponse.created ? 'bookmarkAdded' : 'bookmarkUpdated';

        setParamsObject({
            bookmark_id: updatedBookmark.uuid,
            bookmark_creator: updatedBookmark.creator,
        });
        dispatch(
            setActionType(
                updateBookmarkData(saveBookmarkResponse, { bookmark_id: updatedBookmark.uuid }, bookmarkEventType),
                reportsWithoutConfig.creativeGallery
            )
        );
        triggerBookmarkEvent(bookmarkEventType, saveBookmarkResponse.bookmark);
    }, [saveBookmarkResponse, dispatch]);

    const handleBookmarkSave = bookmark => {
        if (!bookmark.name) {
            setBookmarkValidationError(translate(BOOKMARK_MANDATORY_NAME_ERROR));
            return;
        }

        setBookmarkValidationError();

        saveBookmark(
            ReportsAPI.saveBookmark({
                ...bookmark,
                query: getParamsObject(),
                id: bookmarkId,
            })
        );
    };

    const togglePopover = useCallback(() => {
        setShowBookmarkPopover(prev => !prev);
    }, []);

    const bookmarkTooltip = translate(
        disabled
            ? BOOKMARKS_NOT_AVAILABLE
            : !hasQueryBeenDone
            ? BOOKMARK_RUN_QUERY_MESSAGE
            : bookmarkCreator && userName !== bookmarkCreator
            ? UNAUTHORIZED_BOOKMARK_EDITING
            : ''
    );

    return (
        <Tooltip className={css.tooltip} title={bookmarkTooltip} position="bottom">
            <div ref={saveIconRef} className={css.saveBookmarkContainer}>
                <Icon
                    disabled={!!bookmarkTooltip}
                    name="save-2-line"
                    type={IconTypes.MINGCUTE}
                    onClick={togglePopover}
                />
                <BookmarkPopover
                    type="creativeGallery"
                    show={showBookmarkPopover && !disabled}
                    onSaveBookmark={handleBookmarkSave}
                    error={bookmarkValidationError || saveBookmarkResponseError}
                    bookmarkData={!activeBookmark.name ? null : activeBookmark}
                    startDate={startDate}
                    endDate={endDate}
                />
            </div>
        </Tooltip>
    );
};

BookmarkPopoverContainer.propTypes = {
    translate: PropTypes.func,
    hasQueryBeenDone: PropTypes.bool,
    activeBookmark: PropTypes.shape({
        uuid: PropTypes.string,
        name: PropTypes.string,
        creator: PropTypes.string,
    }),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    disabled: PropTypes.bool,
};

export default withLocalize(BookmarkPopoverContainer);
